import { Typography } from '@mui/material';
import AppShellLoader from 'components/AppShellLoader';
import { LoadingStage } from 'components/Loader';
import { Permission } from 'interfaces/permissionOption';
import React, { useSyncExternalStore } from 'react';
import { Location, Navigate, NavigateFunction, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from 'redux/hooks';
import AuthenticationErrorScreen from 'components/Auth/AuthenticationErrorScreen';
import sessionCoordinator from 'utils/auth/sessionCoordinator';
import { AUTH_SESSION_STATUS } from 'utils/auth/sessionTypes';
import { isAuthenticationError } from 'utils/httpError';
import { usePermissions } from 'utils/usePermissions';

interface Props {
  component: React.ComponentType<React.PropsWithChildren<RouteProps>>;
  isAuthenticated: boolean;
  permissionsRequired?: Permission[]; // if not provided, all roles except disallowed by default are allowed
}

const disallowedByDefault = [Permission.ViewAccessionDashboard];

const defaultRoute = '/';

export interface RouteProps {
  location: Location;
  navigate: NavigateFunction;
  params: Record<string, string>;
}

const PrivateRoute: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  component: Component,
  isAuthenticated,
  permissionsRequired,
}) => {
  const user = useAppSelector((state) => state.auth);
  const sessionStatus = useSyncExternalStore(
    (listener) => sessionCoordinator.subscribe(listener),
    () => sessionCoordinator.getSessionStatus(),
    () => sessionCoordinator.getSessionStatus()
  );
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const { permissions, isLoading, hasSomePermissions, isError, error } = usePermissions();
  const isUnauthorizedError = isError && isAuthenticationError(error);

  if (!isAuthenticated || !user) {
    if (sessionStatus === AUTH_SESSION_STATUS.authenticationError) {
      return <AuthenticationErrorScreen />;
    }
    return <AppShellLoader stage={LoadingStage.SigningIn} />;
  }

  if (isUnauthorizedError) {
    return (
      <Typography variant="h6" color="error">
        Error loading permissions. Please try again later.
      </Typography>
    );
  }

  if (isLoading || (!isError && permissions === undefined)) {
    return <AppShellLoader stage={LoadingStage.CheckingAccess} />;
  }

  if (isError) {
    return (
      <Typography variant="h6" color="error">
        Error loading permissions. Please try again later.
      </Typography>
    );
  }

  const hasExplicitPermissionRequirement = Boolean(permissionsRequired?.length);
  const hasRequiredPermission = hasExplicitPermissionRequirement ? hasSomePermissions(permissionsRequired || []) : true;
  const defaultDisallowed = !hasExplicitPermissionRequirement && hasSomePermissions(disallowedByDefault);

  if (hasExplicitPermissionRequirement && !hasRequiredPermission) {
    return <Navigate to={defaultRoute} replace state={{ from: location }} />;
  }

  if (defaultDisallowed) {
    return <Navigate to={defaultRoute} replace state={{ from: location }} />;
  }

  return <Component location={location} navigate={navigate} params={params} />;
};

export default PrivateRoute;
